<script setup>
import { ref, nextTick, onMounted } from 'vue'
import { $router } from '@/router'
import { $bus } from '@/common/events'
import { dialog } from '@/common/dialog'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import Fullscreen from '@/components/Fullscreen.vue'

const crosshair = `
  <svg width="100" height="100">
    <line x1="0" y1="50" x2="100" y2="50" style="stroke:rgb(255,0,0);stroke-width:2" />
    <line x1="50" y1="0" x2="50" y2="100" style="stroke:rgb(255,0,0);stroke-width:2" />
    <circle cx="50" cy="50" r="30" style="fill:transparent;stroke:rgb(255,0,0);stroke-width:2" />
  </svg>`;

const info = ref("");

const isPixelTest = ref(false);
const isNoSignalTest = ref(false);
const isVideoTest = ref(false);
const isTouchTest = ref(false);
const isPerformanceTest = ref(false);

const index = ref(0);
const colors = ref([
  "#000000",
  "#ffffff",
  "#ff0000",
  "#00ff00",
  "#0000ff",
]);

const markers = ref([ /* { x, y } */ ]);

function startPixelTest() {
  index.value = 0;
  isPixelTest.value = true;
}

function nextColor() {
  if (index.value + 1 >= colors.value.length) {
    //Stop the test
    isPixelTest.value = false;
  }
  else {
    //Next color
    index.value += 1;
  }
}

function startNoSignalTest() {
  isNoSignalTest.value = true;
}

function stopNoSignalTest() {
  isNoSignalTest.value = false;
}

async function startVideoTest() {
  isVideoTest.value = true;

  await nextTick();

  let player = document.getElementById("player");
  player.width = window.innerWidth;
  player.height = window.innerHeight;
}

function stopVideoTest() {
  isVideoTest.value = false;
}

function startTouchTest() {
  markers.value = [];
  isTouchTest.value = true;
}

function stopTouchTest() {
  isTouchTest.value = false;
}

function addMarker(e) {
  markers.value.push({
    x: e.pageX,
    y: e.pageY,
  });
}

function startPerformanceTest() {
  isPerformanceTest.value = true;
}

function stopPerformanceTest() {
  isPerformanceTest.value = false;
}

function goBack() {
  try {
    $router.push({ name: "Admin" });
  }
  catch (err) {
    dialog(err);
  }
}

onMounted(() => {
  useTitle("Display");
  useMenu("admin");
  useSubmenu(false);
  useTopBar(true);

  info.value = window.innerWidth + "x" + window.innerHeight;
})
</script>

<template>
  <div class="relative min-h-screen dark:bg-gray-900 dark:text-white p-5">

    <!-- Header -->
    <div class="w-full mx-auto flex items-center justify-between">
      <div class="text-lg font-bold dark:text-white">{{ $t('admin.display_title') }}</div>
      <div class="flex flex-row justify-items-strech space-x-2">
        <Button type="secondary" @click="goBack()">{{ $t('generic.back') }}</Button>
      </div>
    </div>

    <div class="grid place-items-start mt-4">
      
      <div class="flex flex-col gap-4">

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ $t('admin.window_size') }}</label>
          <TextBox v-model="info" readonly="true"></TextBox>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ $t('admin.check_missing_pixels') }}</label>
          <Button @click="startPixelTest">{{ $t('admin.start_pixel_test') }}</Button>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ $t('admin.check_color_contrast') }}</label>
          <Button @click="startNoSignalTest">{{ $t('admin.show_test_pattern') }}</Button>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ $t('admin.check_touch_accuracy') }}</label>
          <Button @click="startTouchTest">{{ $t('admin.start_touch_test') }}</Button>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ $t('admin.check_video_performance') }}</label>
          <Button @click="startVideoTest">{{ $t('admin.start_video_test') }}</Button>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ $t('admin.check_particle_performance') }}</label>
          <Button @click="startPerformanceTest">{{ $t('admin.start_fireworks') }}</Button>
        </div>

      </div>
      
    </div>

    <Fullscreen v-if="isPixelTest">
      <div class="h-screen w-full bg-white" :style="{ 'background-color': colors[index] }" @click="nextColor"></div>
    </Fullscreen>

    <Fullscreen v-if="isNoSignalTest">
      <div class="h-screen w-full bg-white grid place-items-center" @click="stopNoSignalTest">
        <img src="@/assets/img/no-signal.svg" alt="No signal" />
      </div>
    </Fullscreen>

    <Fullscreen v-if="isVideoTest">
      <div class="relative bg-black">
        <video id="player" width="640" height="480" controls>
          <source src="https://p5js.omara.plt.si/video/sample.mp4" type="video/mp4"><!-- 1080p at 60 FPS -->
        Your browser does not support the video tag.
        </video>
        <Button @click="stopVideoTest" class="absolute top-3 right-3"><i class="fa fa-close"></i></Button>
      </div>
    </Fullscreen>

    <Fullscreen v-if="isTouchTest">
      <div class="h-screen w-full bg-white" style="position: relative;">
        <div class="h-screen w-full grid grid-cols-3 gap-2 items-center justify-items-center">

          <div>
            <div v-html="crosshair"></div>
          </div>
          <div></div>
          <div>
            <div v-html="crosshair"></div>
          </div>

          <div></div>
          <div>
            <div v-html="crosshair"></div>
          </div>
          <div></div>

          <div>
            <div v-html="crosshair"></div>
          </div>
          <div></div>
          <div>
            <div v-html="crosshair"></div>
          </div>

        </div>

        <div class="absolute left-0 top-0 h-screen w-full z-10 bg-transparent" @mousedown="addMarker" @dblclick="stopTouchTest">
          <svg height="100" width="100" class="absolute pointer-events-none" :style="{ 'left': (marker.x - 50) + 'px', 'top': (marker.y - 50) + 'px' }" v-for="marker in markers">
            <circle cx="50" cy="50" r="30" style="fill:yellow;opacity:0.3;" />
          </svg>
          <div class="text-slate-400" v-for="marker in markers">
            {{ marker }}
          </div>
        </div>

      </div>
    </Fullscreen>

    <Fullscreen v-if="isPerformanceTest">
      <div class="relative bg-black">
        <iframe src="https://p5js.omara.plt.si/" style="position:fixed; top:0; left:0; bottom:0; right:0; width:100%; height:100%; border:none; margin:0; padding:0;"></iframe>
        <Button @click="stopPerformanceTest" class="absolute top-3 right-3"><i class="fa fa-close"></i></Button>
      </div>
    </Fullscreen>
    
  </div>
</template>

<style scoped>
</style>
